/**
 * Stałe silnika synchronizacji. Wartości sprawdzone w boju w watchparty —
 * nie zmieniaj ich bez pomiaru na realnym pokoju z co najmniej trzema klientami.
 *
 * Model: jeden leader nadaje swój czas, followerzy korygują dryf, a różnica
 * zegarów klient-serwer jest mierzona i kompensowana (patrz computeExpectedTime).
 */

/** Jak często leader wysyła swój stan na serwer. */
export const LEADER_BROADCAST_MS = 3000;

/** Jak często follower sprawdza dryf względem oczekiwanego czasu. */
export const FOLLOWER_TICK_MS = 1000;

/** Poniżej tego odchylenia (sekundy) NIE seekujemy — seek jest widoczny i drażniący. */
export const DRIFT_THRESHOLD = 2;

/** Ile par time:ping/time:pong na jeden pomiar offsetu zegara. */
export const CLOCK_PROBES = 3;

/** Odstęp między sondami zegara. */
export const CLOCK_PROBE_GAP_MS = 100;

/** Kontrola bezpieczeństwa po zmianie materiału — łapie klienty, które utknęły. */
export const POST_CHANGE_CHECK_MS = 10_000;

/** Odchylenie, przy którym ta kontrola wymusza seek i pokazuje toast. */
export const POST_CHANGE_TOLERANCE = 5;

/** Jak często sprawdzamy, czy leader ma jeszcze żywy socket. */
export const AUTOLEAD_PROBE_MS = 15_000;

/** Karencja przed zniszczeniem pustego pokoju — chroni przed reloadem strony. */
export const EMPTY_ROOM_GRACE_MS = 30_000;

/** Odliczanie przed automatycznym przejściem do następnej pozycji; 0 = natychmiast. */
export const ADVANCE_COUNTDOWN_SEC = 8;

/** Pauza po dojściu odliczania do zera, żeby "0" było widoczne. */
export const ADVANCE_FINALE_MS = 700;

/** Ostrzeżenie przed wygaśnięciem pokoju tymczasowego. */
export const EXPIRY_WARN_MS = 5 * 60_000;

/** Throttle emisji postępu pobierania — 500 ms wystarcza dla płynnego paska. */
export const PROGRESS_EMIT_MS = 500;

/**
 * Ułamek uczestników, którzy muszą zgłosić koniec materiału, by wywołać przejście.
 * Kworum chroni przed pojedynczym klientem, któremu odtwarzacz padł.
 */
export const ENDED_QUORUM_RATIO = 0.5;

export function endedQuorum(userCount: number): number {
  return Math.max(1, Math.floor(userCount * ENDED_QUORUM_RATIO));
}

/**
 * Czas, do którego follower powinien dążyć.
 *
 * `syncTime` to czas leadera w chwili `syncEmittedAt` (stempel serwera). Do niego
 * dodajemy czas, który upłynął od tamtej chwili, korygując różnicę zegarów
 * (`clockOffset` = serverNow - clientNow, mediana z CLOCK_PROBES sond).
 * Przy pauzie nie ekstrapolujemy — czas stoi.
 */
export function computeExpectedTime(args: {
  syncTime: number;
  syncEmittedAt: number | null;
  isPaused: boolean;
  clockOffset: number;
  now?: number;
}): number {
  const { syncTime, syncEmittedAt, isPaused, clockOffset } = args;
  if (isPaused || syncEmittedAt === null) return syncTime;
  const now = args.now ?? Date.now();
  const elapsedMs = Math.max(0, now - syncEmittedAt + clockOffset);
  return syncTime + elapsedMs / 1000;
}

/**
 * Offset zegara z zestawu sond. Dla każdej sondy rtt = odbiór - nadanie,
 * a offset = serverNow - (clientNow + rtt/2), zakładając symetryczne opóźnienie.
 * Bierzemy medianę, bo pojedyncza sonda potrafi trafić na zacięcie sieci.
 */
export function medianClockOffset(
  samples: Array<{ clientNow: number; serverNow: number; receivedAt: number }>,
): number {
  if (samples.length === 0) return 0;
  const offsets = samples
    .map((s) => {
      const rtt = s.receivedAt - s.clientNow;
      return s.serverNow - (s.clientNow + rtt / 2);
    })
    .sort((a, b) => a - b);
  const mid = Math.floor(offsets.length / 2);
  if (offsets.length % 2 === 1) return offsets[mid] ?? 0;
  return ((offsets[mid - 1] ?? 0) + (offsets[mid] ?? 0)) / 2;
}
